import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { http } from "../axios/axios";
import { DELHI_NCR_CITIES, serviceCityPages } from "../data/seoLandingData";

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const formatCityName = (value) =>
  value
    ? value
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "";

export default function CityServiceLandingPage() {
  const { service, city } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const serviceKey = service || "cctv-installation";
  const cityKey = city || "delhi";

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);

      try {
        const res = await http.get(`/services/location/${serviceKey}/${cityKey}`);
        const cmsData = res?.data?.data;

        if (cmsData) {
          setPageData({
            ...cmsData,
            slug: cmsData.slug || `${serviceKey}/${cityKey}`,
            city: cmsData.city || formatCityName(cityKey),
            title: cmsData.title || `${formatCityName(serviceKey)} in ${formatCityName(cityKey)} | Suraksha 24X7`,
            description: cmsData.description || "Professional security services in Delhi NCR.",
            h1: cmsData.h1 || `${formatCityName(serviceKey)} in ${formatCityName(cityKey)}`,
            intro: cmsData.intro || "Professional installation and support for modern security systems.",
            highlights: cmsData.highlights || [],
            benefits: cmsData.benefits || [],
            faqs: cmsData.faqs || [],
          });
          return;
        }
      } catch (error) {
        console.warn("Unable to load CMS location page, falling back to static content:", error);
      }

      const staticData = serviceCityPages[serviceKey]?.[cityKey];
      if (staticData) {
        setPageData(staticData);
      } else {
        const fallbackCity = DELHI_NCR_CITIES.includes(cityKey) ? cityKey : "delhi";
        setPageData(serviceCityPages[serviceKey]?.[fallbackCity] || null);
      }
      setLoading(false);
    };

    loadPage();
  }, [serviceKey, cityKey]);

  useEffect(() => {
    if (pageData) setLoading(false);
  }, [pageData]);

  if (loading) {
    return (
      <main className="flex min-h-[40vh] items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
      </main>
    );
  }

  if (!pageData) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-slate-900">Page not found</h1>
        <p className="mt-3 text-slate-600">
          This service location page is not available yet.
        </p>
        <Link
          to="/contact"
          className="mt-6 inline-block rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white"
        >
          Request a quote
        </Link>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageData.title}</title>
        <meta name="description" content={pageData.description} />
        <meta
          name="keywords"
          content={`${pageData.city}, ${capitalize(serviceKey)}, security system, surveillance installation, Suraksha 24X7`}
        />
        <link
          rel="canonical"
          href={`https://www.suraksha24x7.com/${pageData.slug}`}
        />
      </Helmet>

      <main className="bg-white text-slate-900">
        <section className="bg-slate-950 text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:py-24">
            <div>
              <p className="mb-4 inline-flex rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-blue-200">
                Suraksha 24X7
              </p>
              <h1 className="text-4xl font-bold leading-tight md:text-5xl">
                {pageData.h1}
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-slate-300">
                {pageData.intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-500"
                >
                  Get free consultation
                </Link>
                <Link
                  to="/product-inquiry"
                  className="rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Request site survey
                </Link>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl shadow-blue-900/20 backdrop-blur-sm">
              <h2 className="text-2xl font-semibold text-white">
                Why businesses choose us
              </h2>
              <ul className="mt-6 space-y-4 text-slate-200">
                {pageData.highlights.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {pageData.benefits.map((benefit) => (
              <div
                key={benefit}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-lg font-bold text-blue-700">
                  ✓
                </div>
                <p className="text-lg font-semibold text-slate-900">
                  {benefit}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">
                Service coverage
              </p>
              <h2 className="mt-3 text-3xl font-bold text-slate-900">
                Security solutions tailored for {pageData.city}
              </h2>
            </div>

            <div className="mt-10 grid gap-8 lg:grid-cols-2">
              {[
                "Property assessment and camera placement planning",
                "Professional installation with clean wiring and testing",
                "Remote monitoring setup and mobile access configuration",
                "Support for maintenance, upgrades and system diagnostics",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <p className="text-base font-medium text-slate-800">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold text-slate-900">
              Common questions
            </h2>
          </div>
          <div className="mt-10 space-y-5">
            {pageData.faqs.map((item) => (
              <div
                key={item.q}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-slate-900">
                  {item.q}
                </h3>
                <p className="mt-2 text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-slate-900 py-16 text-white">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold md:text-4xl">
              Protect your property with a dependable security system
            </h2>
            <p className="mt-4 text-slate-300">
              Discuss your site requirements with our team and get a practical
              solution for your location.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500"
              >
                Book a consultation
              </Link>
              <Link
                to="/security-product"
                className="rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/5"
              >
                Explore products
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
